import { createStyles, Stack } from '@mantine/core';
import type { ERDFlowColumn } from '@repo/api-codegen';
import { observer } from 'mobx-react-lite';
import { useERDFlowContext } from '../../context/ERDFlowProvider/ERDFlowContext';
import {
	ENTITY_NODE_PADDING,
	ENTITY_NODE_WIDTH,
} from '../EntityNode/EntityNode.constants';
import { ENTITY_CHILDREN_ROW_HEIGHT } from '../EntityNodeChildren/EntityNodeChildren.constants';
import TableNodeColumn from './TableNodeColumn';

interface TableNodeColumnsProps {
	columnsWithRelationships: ERDFlowColumn[];
	columnsWithoutRelationships: ERDFlowColumn[];
}

const useStyles = createStyles((theme) => ({
	wrapper: {
		cursor: 'default',
		position: 'relative',
		width: ENTITY_NODE_WIDTH,
		height: '100%',
		padding: ENTITY_NODE_PADDING,
		borderTop: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		gap: 0,
	},
	control: {
		cursor: 'pointer',
		height: ENTITY_CHILDREN_ROW_HEIGHT,
		paddingLeft: theme.spacing.xs,
		paddingRight: theme.spacing.xs,
		borderRadius: theme.radius.sm,
		gap: theme.spacing['3xs'],
		backgroundColor: theme.other.getColor('surface/secondary/default'),
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/secondary/hover'),
		},
		'&:active': {
			backgroundColor: theme.other.getColor('surface/secondary/active'),
		},
	},
}));

const TableNodeColumns = observer(
	({
		columnsWithRelationships,
		columnsWithoutRelationships,
	}: TableNodeColumnsProps) => {
		const { classes } = useStyles();

		const { activeColumnId, highlightedColumnIds } = useERDFlowContext();

		return (
			<Stack className={classes.wrapper}>
				{columnsWithRelationships.map((column, order) => (
					<TableNodeColumn
						key={column.id}
						column={column}
						order={order}
						focused={activeColumnId === column.id}
						highlighted={highlightedColumnIds.has(column.id)}
					/>
				))}
				{columnsWithoutRelationships.map((column, order) => (
					<TableNodeColumn
						key={column.id}
						column={column}
						order={order}
						disabled
					/>
				))}
			</Stack>
		);
	}
);

export default TableNodeColumns;
